import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import {
  AccessRequirementFacade,
  MtnaAccessRequirementDialogComponent,
  MtnaAccessRequirementDialogData,
  MtnaAccessRequirementService,
} from '@mtna/web-security-angular';
import { map, switchMap, take } from 'rxjs/operators';
import { RdsApiService, RdsCatalogService } from '@rds/shared-api';
import { Observable, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Ensure the user has completed all the access requirements necessary to access a particular resource in the
 * application.
 *
 * @author Andrew DeCarlo <andrew.decarlo@mtna.us>
 */
@Injectable({ providedIn: 'root' })
export class RdsAccessRequirementGuard implements CanLoad, CanActivate {
  constructor(
    private dialog: MatDialog,
    private accessRequirementService: MtnaAccessRequirementService,
    private accessRequirementFacade: AccessRequirementFacade,
    private catalogService: RdsCatalogService,
    private rdsApiService: RdsApiService,
    private router: Router,
    private snackbar: MatSnackBar
  ) {}

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> {
    let catalog = this.rdsApiService.catalogId;
    let dataProduct = this.rdsApiService.dataProductId;
    if ((!catalog || !dataProduct) && segments.length > 2) {
      catalog = segments[1].toString();
      dataProduct = segments[2].toString();
    }
    return this.checkAccessRequirements(catalog, dataProduct, '/' + segments.join('/'));
  }

  /**
   * This method will evaluate the catalog and data product IDs that are found in the route to determine if there
   * are outstanding access requirements that must be taken care of before the route can be activated. It will use
   * the ID's to resolve the data product URI and then use that to interrogate the access service for any necessary
   * access requirements.
   *
   * @param route the route which should contain the catalog and data product IDs
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    let catalog = route.paramMap.get('catalogId');
    let dataProduct = route.paramMap.get('dataProductId');

    // if the catalog and data product id are not in param map try parsing url
    if (!catalog || !dataProduct) {
      const url = state.url.split('/');
      if (url.length > 4) {
        catalog = url[2];
        dataProduct = url[3];
      }
    }
    return this.checkAccessRequirements(catalog, dataProduct, state.url);
  }

  private checkAccessRequirements(
    catalog: string | undefined | null,
    dataProduct: string | undefined | null,
    redirect: string
  ): Observable<boolean | UrlTree> {
    if (!catalog || !dataProduct) {
      this.snackbar.open($localize`Please select a data product to continue.`, $localize`DISMISS`, {
        duration: 8000,
      });
      return of(this.router.createUrlTree(['/data-product-selection']));
    }

    return this.catalogService.getDataProductUri(catalog, dataProduct).pipe(
      take(1),
      switchMap((uri) => {
        return this.accessRequirementService.getResourceAccessRequirements(uri).pipe(
          take(1),
          map((accessRequirements) => {
            const canActivate = !accessRequirements || accessRequirements.length == 0;
            if (!canActivate) {
              this.accessRequirementFacade.setRoutingConfiguration(redirect ? redirect : '/', false, '/');
              this.dialog.open(MtnaAccessRequirementDialogComponent, {
                disableClose: true,
                hasBackdrop: true,
                minWidth: '100ch',
                maxWidth: '70vw',
                data: new MtnaAccessRequirementDialogData(accessRequirements, false),
              });
            }
            return canActivate;
          })
        );
      })
    );
  }
}
